/**
 * WhereRailView — Rail rendering of a WHERE/HAVING `BooleanNode` tree.
 * Read-only (3B.2): connector "rail" + stacked inert leaf chips.
 * Editable (3B.3+): click-to-edit/remove leaves, group toolbar (3B.4),
 * "Add condition"/"+ group" affordances, select checkboxes + wrap button (3B.5),
 * and per-group dnd-kit drag-reorder of children (3B.6).
 *
 * HAVING parity (Plan 3 Task 5): `kind` (default `'where'`) parameterizes the
 * testids (`vqe-${kind}-rail-*`), the two rail analytics events
 * (`feature:vqe_${kind}_group_edit` / `_node_edit`), and the `clause` param
 * threaded into every tree-op wrapper call — so the SAME component serves
 * both clauses and the default keeps WHERE byte-identical.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';

import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import type { BooleanNode } from '../../../../sql-resolver/types';
import type { BooleanClauseTarget } from '../../../../sql-resolver/types/structuralEdit';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { moveNode } from '../../edit/edits/moveNode';
import { wrapGroup } from '../../edit/edits/wrapGroup';
import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import { RailAddCondition } from './RailAddCondition';
import { RailAddGroup } from './RailAddGroup';
import { RailChildRow } from './RailChildRow';
import { RailGroupToolbar } from './RailGroupToolbar';
import { RailLeafChip, type RailSubquery } from './RailLeafChip';
import { isWrappable, toggleSelection, type RailSelection } from './railSelection';

type GroupNode = BooleanNode & { kind: 'group' };

// Props threaded through every rail node: shift-select state (3B.5) + the
// optional subquery-drill context (3B.7) that lets IN(SELECT …) leaves drill +
// the clause `kind` (Plan 3 Task 5, default 'where').
type SelectProps = {
  readonly selection?: RailSelection;
  readonly onToggleSelect?: (parentPath: readonly number[], index: number, shift: boolean) => void;
  readonly subquery?: RailSubquery;
  readonly kind?: BooleanClauseTarget;
};

const CONNECTOR_CLASS: Record<'AND' | 'OR', string> = {
  AND: 'border-blue-500/40 bg-blue-500/15 text-blue-600 dark:text-blue-400',
  OR: 'border-amber-500/40 bg-amber-500/15 text-amber-700 dark:text-amber-400',
};

const RAIL_BAR_CLASS: Record<'AND' | 'OR', string> = {
  AND: 'border-blue-500/50',
  OR: 'border-amber-500/50',
};

export function WhereRailView({
  root,
  editContext,
  subquery,
  kind = 'where',
}: {
  readonly root: BooleanNode;
  readonly editContext?: VqeEditContext;
  readonly subquery?: RailSubquery;
  readonly kind?: BooleanClauseTarget;
}): ReactElement {
  const { track } = useAnalytics();
  const [selection, setSelection] = useState<RailSelection>(null);
  const onToggleSelect = (parentPath: readonly number[], index: number, shift: boolean): void =>
    setSelection((prev) => toggleSelection(prev, parentPath, index, shift));
  const wrappable = editContext && isWrappable(selection) ? selection : null;

  return (
    <div className="font-mono text-xs" data-testid={`vqe-${kind}-rail`}>
      {wrappable ? (
        <div className="mb-1.5">
          <button
            type="button"
            data-testid={`vqe-${kind}-rail-wrap-group`}
            className="rounded border border-border bg-muted/60 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-foreground hover:bg-muted"
            onClick={() => {
              void wrapGroup(
                wrappable.parentPath,
                wrappable.from,
                wrappable.to,
                toCommitCtx(editContext!),
                kind,
              );
              track(`feature:vqe_${kind}_group_edit`, { action: 'wrap' });
              setSelection(null);
            }}
          >
            ( ) Group
          </button>
        </div>
      ) : null}
      <NodeView
        node={root}
        path={[]}
        editContext={editContext}
        selection={selection}
        onToggleSelect={onToggleSelect}
        subquery={subquery}
        kind={kind}
      />
    </div>
  );
}

function NodeView({
  node,
  path,
  editContext,
  selection,
  onToggleSelect,
  subquery,
  kind,
}: {
  readonly node: BooleanNode;
  readonly path: readonly number[];
  readonly editContext?: VqeEditContext;
} & SelectProps): ReactElement {
  if (node.kind === 'leaf') {
    return (
      <RailLeafChip node={node} path={path} editContext={editContext} subquery={subquery} kind={kind} />
    );
  }
  return (
    <GroupRail
      node={node}
      path={path}
      editContext={editContext}
      selection={selection}
      onToggleSelect={onToggleSelect}
      subquery={subquery}
      kind={kind}
    />
  );
}

function GroupRail({
  node,
  path,
  editContext,
  selection,
  onToggleSelect,
  subquery,
  kind = 'where',
}: {
  readonly node: GroupNode;
  readonly path: readonly number[];
  readonly editContext?: VqeEditContext;
} & SelectProps): ReactElement {
  return (
    <div className="flex">
      <div
        className={`flex flex-none flex-col items-center justify-center gap-1 border-l-2 px-2 ${RAIL_BAR_CLASS[node.connector]}`}
      >
        <span
          className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${CONNECTOR_CLASS[node.connector]}`}
          data-testid={`vqe-${kind}-rail-connector`}
          data-connector={node.connector}
          data-negated={node.negated ? 'true' : 'false'}
        >
          {node.negated ? `NOT ${node.connector}` : node.connector}
        </span>
        {editContext ? (
          <RailGroupToolbar node={node} path={path} editContext={editContext} kind={kind} />
        ) : null}
      </div>
      <div className="flex flex-1 flex-col gap-2 py-1">
        {editContext ? (
          <GroupChildren
            node={node}
            path={path}
            editContext={editContext}
            selection={selection}
            onToggleSelect={onToggleSelect}
            subquery={subquery}
            kind={kind}
          />
        ) : (
          node.children.map((child, idx) => (
            <NodeView
              key={`${child.kind}-${idx}`}
              node={child}
              path={[...path, idx]}
              subquery={subquery}
              kind={kind}
            />
          ))
        )}
        {editContext ? (
          <div className="flex flex-wrap gap-2">
            <RailAddCondition
              groupPath={path}
              connector={node.connector}
              editContext={editContext}
              kind={kind}
            />
            <RailAddGroup groupPath={path} connector={node.connector} editContext={editContext} kind={kind} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

// Editable children with per-group drag-reorder. Each group is its own
// DndContext, so drags are scoped to a single group's sibling list.
function GroupChildren({
  node,
  path,
  editContext,
  selection,
  onToggleSelect,
  subquery,
  kind = 'where',
}: {
  readonly node: GroupNode;
  readonly path: readonly number[];
  readonly editContext: VqeEditContext;
} & SelectProps): ReactElement {
  const { track } = useAnalytics();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = Number(active.id);
    const to = Number(over.id);
    if (!Number.isInteger(from) || !Number.isInteger(to)) return;
    void moveNode([...path, from], path, to, toCommitCtx(editContext), kind);
    track(`feature:vqe_${kind}_node_edit`, { action: 'move' });
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext
        items={node.children.map((_, idx) => String(idx))}
        strategy={verticalListSortingStrategy}
      >
        {node.children.map((child, idx) => (
          <RailChildRow
            key={`${child.kind}-${idx}`}
            parentPath={path}
            index={idx}
            selection={selection}
            onToggleSelect={onToggleSelect}
            kind={kind}
          >
            <NodeView
              node={child}
              path={[...path, idx]}
              editContext={editContext}
              selection={selection}
              onToggleSelect={onToggleSelect}
              subquery={subquery}
              kind={kind}
            />
          </RailChildRow>
        ))}
      </SortableContext>
    </DndContext>
  );
}
